import React, { useState } from 'react';
import styled from 'styled-components';
import { useWalletClient, usePublicClient } from 'wagmi';
import { parseUnits } from 'viem';
import { resolveZNSName, isValidAddress } from '../../utils/zns';
import { logMavrkPayTransaction } from '../../utils/supabase';
import RecentTransactions from './RecentTransactions';

const USDG_ADDRESS = '0xe343167631d89B6Ffc58B88d6b7fB0228795491D' as const;
const INK_CHAIN_ID = 57073;

interface PayTabProps {
  userAddress: `0x${string}` | undefined;
  balance: string;
  onSuccess: () => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
`;

const Input = styled.input`
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 10px;
  color: #ffffff;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  
  &:focus {
    outline: none;
    border-color: rgba(168, 85, 247, 0.6);
    background: rgba(0, 0, 0, 0.4);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SendButton = styled.button`
  padding: 14px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.3), rgba(124, 58, 237, 0.3));
  border: 1px solid rgba(168, 85, 247, 0.6);
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 12px;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(168, 85, 247, 0.5), rgba(124, 58, 237, 0.5));
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(168, 85, 247, 0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: -4px;
`;

const ResolvedAddress = styled.div`
  font-size: 12px;
  color: #10b981;
  margin-top: 4px;
  font-family: 'monospace';
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;
`;

const SuccessText = styled.div`
  padding: 12px 16px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 10px;
  color: #10b981;
  font-size: 13px;
`;

const PayTab: React.FC<PayTabProps> = ({ userAddress, balance, onSuccess }) => {
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [resolvedAddress, setResolvedAddress] = useState<string | null>(null);
  const [inputType, setInputType] = useState<'wallet' | 'domain' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();

  const USDG_ABI = [
    {
      name: 'transfer',
      type: 'function',
      stateMutability: 'nonpayable',
      inputs: [
        { name: 'to', type: 'address' },
        { name: 'value', type: 'uint256' }
      ],
      outputs: [{ name: '', type: 'bool' }]
    }
  ] as const;

  // Auto-detect input type and resolve ZNS domains with debouncing
  React.useEffect(() => {
    const trimmedRecipient = recipient.trim();
    
    if (!trimmedRecipient) {
      setResolvedAddress(null);
      setInputType(null);
      setError(null);
      return;
    }

    // If starts with 0x, it's a wallet address
    if (trimmedRecipient.startsWith('0x')) {
      setInputType('wallet');
      setResolvedAddress(null);
      
      // Validate wallet address
      if (isValidAddress(trimmedRecipient)) {
        setError(null);
      } else if (trimmedRecipient.length >= 42) {
        setError('Invalid wallet address format');
      }
      return;
    }

    // Otherwise, treat as ZNS domain name
    setInputType('domain');
    
    // Debounce ZNS resolution
    const timeoutId = setTimeout(async () => {
      if (trimmedRecipient.length >= 3) {
        try {
          // Auto-append .ink if not present and normalize to lowercase
          const normalizedRecipient = trimmedRecipient.toLowerCase();
          const domainToResolve = normalizedRecipient.endsWith('.ink') 
            ? normalizedRecipient 
            : `${normalizedRecipient}.ink`;
          
          const address = await resolveZNSName(domainToResolve);
          if (address) {
            setResolvedAddress(address);
            setError(null);
          } else {
            setResolvedAddress(null);
            // Don't show error while user is still typing
            if (trimmedRecipient.length > 5) {
              setError(`Could not resolve ${domainToResolve}`);
            }
          }
        } catch (err) {
          console.error('ZNS resolution error:', err);
          setResolvedAddress(null);
        }
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [recipient]);

  const handleRecipientChange = (value: string) => {
    setRecipient(value);
    setError(null);
  };

  const handleSend = async () => {
    if (!userAddress || !walletClient) {
      setError('Wallet not connected');
      return;
    }

    if (!recipient || !amount) {
      setError('Please fill in all fields');
      return;
    }

    const amountNum = parseFloat(amount);
    const balanceNum = parseFloat(balance);

    if (amountNum <= 0) {
      setError('Amount must be greater than 0');
      return;
    }

    if (amountNum > balanceNum) {
      setError('Insufficient balance');
      return;
    }

    // Determine the recipient address
    let toAddress: `0x${string}`;
    
    if (inputType === 'wallet') {
      // Direct wallet address
      if (!isValidAddress(recipient)) {
        setError('Invalid wallet address');
        return;
      }
      toAddress = recipient as `0x${string}`;
    } else if (inputType === 'domain' && resolvedAddress) {
      // Resolved ZNS domain
      toAddress = resolvedAddress as `0x${string}`;
    } else {
      setError('Please enter a valid wallet address or .ink domain');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const valueWei = parseUnits(amount, 6); // USDG has 6 decimals

      // Execute standard ERC20 transfer directly
      const hash = await walletClient.writeContract({
        address: USDG_ADDRESS,
        abi: USDG_ABI,
        functionName: 'transfer',
        args: [toAddress, valueWei],
        account: userAddress,
      });

      console.log('Transaction hash:', hash);

      // Wait for transaction confirmation
      let receipt;
      if (publicClient) {
        receipt = await publicClient.waitForTransactionReceipt({ hash });
      }

      console.log('Payment successful!');

      // Log transaction to Supabase for history
      await logMavrkPayTransaction(
        userAddress,
        'pay',
        amount, // Amount in USDG (1 USDG = $1.00)
        toAddress,
        hash,
        receipt ? Number(receipt.blockNumber) : undefined
      );

      setSuccess(true);
      setRecipient('');
      setAmount('');
      setResolvedAddress(null);
      setInputType(null);
      onSuccess(); // Refresh balance

      setTimeout(() => setSuccess(false), 5000);
    } catch (err: any) {
      console.error('Send error:', err);
      setError(err.message || 'Failed to send payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <InputGroup>
        <Label>Recipient</Label>
        <Input
          type="text"
          placeholder="Wallet address (0x...) or .ink domain (deployerone)"
          value={recipient}
          onChange={(e) => handleRecipientChange(e.target.value)}
        />
        {resolvedAddress && (
          <ResolvedAddress>
            ✓ {recipient.toLowerCase().endsWith('.ink') ? recipient.toLowerCase() : `${recipient.toLowerCase()}.ink`} → {resolvedAddress.slice(0, 6)}...{resolvedAddress.slice(-4)}
          </ResolvedAddress>
        )}
        <HelpText>
          {inputType === 'wallet' 
            ? 'Wallet address detected' 
            : inputType === 'domain'
            ? 'Domain name detected'
            : 'Enter wallet address or domain name (e.g., deployerone)'}
        </HelpText>
      </InputGroup>

      <InputGroup>
        <Label>Amount (USD)</Label>
        <Input
          type="number"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          step="0.01"
          min="0"
        />
        <HelpText>Available: {balance} USDG</HelpText>
      </InputGroup>

      {error && <ErrorText>{error}</ErrorText>}
      {success && <SuccessText>Payment sent successfully!</SuccessText>}

      <SendButton onClick={handleSend} disabled={loading || !recipient || !amount}>
        {loading ? 'Sending...' : 'Send Payment'}
      </SendButton>

      <RecentTransactions userAddress={userAddress} type="sent" />
    </Container>
  );
};

export default PayTab;

